import { useRef, useState } from 'react'
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers'
import { useCategories } from '../hooks/useCategories'
import { useFocusTrap } from '../hooks/useFocusTrap'
import { useSidebarConfig } from '../hooks/useSidebarConfig'
import { useAuth } from '../lib/auth'
import { useSharedTeam } from '../hooks/useTeam'
import { getCategoryColor } from '../lib/colors'
import type { Tag } from '../hooks/useTags'
import type { Composite } from '../hooks/useComposites'
import type { CompositeDefinition } from '../lib/compositeSchema'
import { voidAsync } from '../lib/voidAsync'

const TAG_COLOR_KEYS = ['blue', 'purple', 'green', 'red', 'amber', 'orange', 'pink', 'cyan', 'lime', 'violet']

interface OrganizeModalProps {
  onClose: () => void
  tags?: Tag[]
  onAddTag?: (name: string, color: string) => Promise<Tag | null | undefined>
  /**
   * Updates accept any of the editable tag fields. `team_id` gained entry
   * here with ADR 0028 (team-shared tags): pass a uuid to share, `null`
   * to unshare. RLS rejects invalid combinations server-side.
   */
  onUpdateTag?: (tagId: string, updates: Partial<Pick<Tag, 'name' | 'color' | 'icon' | 'sort_order' | 'team_id'>>) => void
  onDeleteTag?: (tagId: string) => void
  onNavigateToFilter?: (filter: string) => void
  // ─── Composites (ADR 0029) ────────────────────────────────────────────────
  // User's saved filters. Optional — Shell passes them in; embedded
  // contexts (e.g. an extension-side mount if we ever build one) can omit.
  composites?: Composite[]
  onAddComposite?: (input: { name: string; icon?: string; color?: string; definition?: CompositeDefinition }) => Promise<Composite | null | undefined>
  onDeleteComposite?: (id: string) => void
  /** Current filter state used to seed a new composite from the active
   * view. The modal doesn't know Shell's routing — Shell composes the
   * current activeFilter + inboxView into a CompositeDefinition and
   * passes it in. */
  currentCompositeDefinition?: CompositeDefinition
}

const EMOJI_GRID = [
  ['📥', '📨', '✉️', '💬', '📩', '🗂️', '📬'],
  ['📁', '📂', '🔔', '⚡', '🎯', '🚀', '💡'],
  ['✅', '❤️', '🔥', '💰', '🌟', '🔗', '💎'],
]

// --- DnD handle icon ---
function DragHandle({ listeners, attributes }: { listeners?: Record<string, Function>; attributes?: Record<string, any> }) {
  return (
    <span {...listeners} {...attributes} className="flex cursor-grab items-center text-zinc-300 opacity-0 group-hover:opacity-100 active:cursor-grabbing">
      <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor">
        <circle cx="3" cy="2" r="1.2"/><circle cx="7" cy="2" r="1.2"/>
        <circle cx="3" cy="7" r="1.2"/><circle cx="7" cy="7" r="1.2"/>
        <circle cx="3" cy="12" r="1.2"/><circle cx="7" cy="12" r="1.2"/>
      </svg>
    </span>
  )
}

// --- Sortable row wrapper ---
type SortableRowRenderArgs = {
  listeners: ReturnType<typeof useSortable>['listeners']
  attributes: ReturnType<typeof useSortable>['attributes']
}
function SortableRow({ id, children, className }: {
  id: string
  children: React.ReactNode | ((args: SortableRowRenderArgs) => React.ReactNode)
  className?: string
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    position: 'relative' as const,
  }
  return (
    <div ref={setNodeRef} style={style} className={`${className || ''} ${isDragging ? 'rounded-lg bg-white shadow-md ring-1 ring-zinc-200' : ''}`}>
      {typeof children === 'function' ? children({ listeners, attributes }) : children}
    </div>
  )
}

// --- Color swatch ---
function ColorSwatch({ color, selected, onClick }: { color: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`relative h-5 w-5 rounded-full border transition-transform hover:scale-110 ${selected ? 'border-zinc-400 ring-1 ring-offset-1 ring-zinc-400' : 'border-zinc-200'}`}
      style={{ backgroundColor: getCategoryColor(color) }}
    >
      {selected && (
        <svg className="absolute inset-0 m-auto" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
    </button>
  )
}

// --- Eye icon toggle ---
function VisibilityToggle({ visible, onClick }: { visible: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`h-4 w-7 rounded-full p-[2px] transition-colors ${visible ? 'bg-zinc-900' : 'bg-zinc-300'}`}
      title={visible ? 'Hide' : 'Show'}
    >
      <div className={`h-3 w-3 rounded-full bg-white transition-transform ${visible ? 'translate-x-[11px]' : 'translate-x-0'}`} />
    </button>
  )
}

export default function OrganizeModal({
  onClose,
  tags = [],
  onAddTag,
  onUpdateTag,
  onDeleteTag,
  onNavigateToFilter,
  composites = [],
  onAddComposite,
  onDeleteComposite,
  currentCompositeDefinition,
}: OrganizeModalProps) {
  const { categories, addCategory, updateCategory, deleteCategory } = useCategories()
  const { items, toggleVisibility, setEmoji, reorder } = useSidebarConfig()
  const { user } = useAuth()
  // Presence of a shared team gates the share-with-team UI — users
  // without a shared team simply don't see the toggle. Added with
  // ADR 0028 (team-shared tags).
  const sharedTeam = useSharedTeam()
  const modalRef = useRef<HTMLDivElement>(null)
  useFocusTrap(modalRef)

  const [newTagName, setNewTagName] = useState('')
  const [newCompositeName, setNewCompositeName] = useState('')
  const [emojiPickerFor, setEmojiPickerFor] = useState<string | null>(null)
  // Separate state from `emojiPickerFor` because tag IDs (uuid) and
  // sidebar-item keys (INBOX/ARCHIVE/...) live in the same namespace
  // otherwise and toggling one would dismiss the other. Added as part
  // of 1.1a icons-per-tag (ADR 0027).
  const [tagEmojiPickerFor, setTagEmojiPickerFor] = useState<string | null>(null)
  const [editingTagColor, setEditingTagColor] = useState<string | null>(null)
  const [editingTagId, setEditingTagId] = useState<string | null>(null)
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null)
  const [editingFolderColor, setEditingFolderColor] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  )

  const handleMessagesDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((i) => i.key === active.id)
      const newIndex = items.findIndex((i) => i.key === over.id)
      reorder(oldIndex, newIndex)
    }
  }

  // Sequential sort_order write so a mid-chain failure doesn't leave a
  // partially-reordered list. Previously we fired all N updates in
  // parallel via `forEach(voidAsync(...))`; each one snapshotted the
  // cache, so one reject rolled back the entire state. With the
  // sequential chain, if update #4 of 20 fails, updates #1-3 have
  // committed and are stable; #5+ simply don't run. Users can retry
  // from the still-correct intermediate state.
  //
  // Short-circuits on the first failure (`await` throws → caught → stop).
  async function applyReorderSequential<Id>(
    ids: Id[],
    updateFn: (id: Id, sortOrder: number) => Promise<unknown> | void,
    label: string,
  ) {
    for (let i = 0; i < ids.length; i++) {
      try {
        const result = updateFn(ids[i], i)
        if (result && typeof (result as Promise<unknown>).then === 'function') {
          await result
        }
      } catch (err) {
        console.warn(`[${label}] reorder stopped at index ${i}:`, err)
        return
      }
    }
  }

  const handleFoldersDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const oldIndex = categories.findIndex((c) => c.id === active.id)
      const newIndex = categories.findIndex((c) => c.id === over.id)
      if (oldIndex === -1 || newIndex === -1) return
      const ids = categories.map((c) => c.id)
      const reordered = [...ids]
      const [moved] = reordered.splice(oldIndex, 1)
      reordered.splice(newIndex, 0, moved)
      voidAsync(
        applyReorderSequential(reordered, (id, i) => updateCategory(id, { sort_order: i }), 'folders-reorder'),
        'folders-reorder',
      )
    }
  }

  const handleTagsDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const oldIndex = tags.findIndex((t) => t.id === active.id)
      const newIndex = tags.findIndex((t) => t.id === over.id)
      if (oldIndex === -1 || newIndex === -1) return
      const ids = tags.map((t) => t.id)
      const reordered = [...ids]
      const [moved] = reordered.splice(oldIndex, 1)
      reordered.splice(newIndex, 0, moved)
      voidAsync(
        applyReorderSequential(reordered, (id, i) => onUpdateTag?.(id, { sort_order: i }), 'tags-reorder'),
        'tags-reorder',
      )
    }
  }

  return (
    <div
      className="animate-fade-in-fast fixed inset-0 z-50 flex items-center justify-center bg-black/20"
      onClick={onClose}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}
      role="dialog"
      aria-modal="true"
      aria-label="Organize sidebar"
    >
      <div
        ref={modalRef}
        className="animate-scale-in relative w-[480px] max-h-[620px] overflow-y-auto rounded-2xl border border-zinc-200 bg-white shadow-[0_8px_40px_rgba(0,0,0,0.12),0_1px_3px_rgba(0,0,0,0.06)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-zinc-100 px-5 py-4">
          <span className="text-sm font-semibold text-zinc-900">Organize Sidebar</span>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-600">
            <span className="text-base">×</span>
          </button>
        </div>

        {/* Messages */}
        <div className="relative px-5 py-3">
          <span className="block pb-2 pt-1 text-[10px] font-medium tracking-wider text-zinc-400">MESSAGES</span>
          <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis, restrictToParentElement]} onDragEnd={handleMessagesDragEnd}>
            <SortableContext items={items.map((i) => i.key)} strategy={verticalListSortingStrategy}>
              {items.map((item) => (
                <SortableRow key={item.key} id={item.key} className="group">
                  {({ listeners, attributes }: any) => (
                    <div className={`flex items-center gap-2.5 rounded-lg px-1.5 py-[7px] hover:bg-zinc-50 ${!item.visible ? 'opacity-30 grayscale' : ''}`}>
                      <DragHandle listeners={listeners} attributes={attributes} />
                      <button
                        onClick={() => setEmojiPickerFor(emojiPickerFor === item.key ? null : item.key)}
                        className={`flex h-6 w-6 items-center justify-center rounded-md text-[13px] ${
                          emojiPickerFor === item.key ? 'border-[1.5px] border-blue-500 bg-blue-50' : 'hover:bg-zinc-100'
                        }`}
                      >
                        {item.emoji}
                      </button>
                      <button
                        onClick={() => onNavigateToFilter?.(item.key)}
                        className="flex-1 truncate text-left text-[12.5px] text-zinc-700 hover:text-blue-600 hover:underline"
                      >
                        {item.label}
                      </button>
                      {item.key === 'INBOX' ? (
                        <div className="w-7" />
                      ) : (
                        <VisibilityToggle visible={item.visible} onClick={() => toggleVisibility(item.key)} />
                      )}
                    </div>
                  )}
                </SortableRow>
              ))}
            </SortableContext>
          </DndContext>

          {/* Floating emoji picker */}
          {emojiPickerFor && (
            <div className="absolute left-14 top-10 z-10 w-60 rounded-xl border border-zinc-200 bg-white p-2.5 shadow-[0_8px_32px_rgba(0,0,0,0.14),0_1px_3px_rgba(0,0,0,0.06)]">
              <div className="mb-2 flex items-center rounded-[7px] bg-zinc-100 px-2.5 py-1.5">
                <span className="text-[11px] text-zinc-400">Search emoji...</span>
              </div>
              {['Frequent', 'Objects', 'Symbols'].map((label, gi) => (
                <div key={label} className="mb-2">
                  <span className="mb-1 block text-[10px] font-medium text-zinc-400">{label}</span>
                  <div className="flex flex-wrap gap-0.5">
                    {EMOJI_GRID[gi].map((emoji) => (
                      <button
                        key={emoji}
                        onClick={() => { setEmoji(emojiPickerFor, emoji); setEmojiPickerFor(null) }}
                        className={`flex h-[30px] w-[30px] items-center justify-center rounded-md text-[15px] hover:bg-zinc-100 ${
                          items.find((i) => i.key === emojiPickerFor)?.emoji === emoji ? 'bg-blue-50' : ''
                        }`}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Folders */}
        <div className="px-5 pb-3 pt-2">
          <div className="flex items-center justify-between pb-2 pt-1">
            <span className="text-[10px] font-medium tracking-wider text-zinc-400">FOLDERS</span>
            <button
              onClick={() => voidAsync(addCategory('New Folder', 'blue'), 'addCategory', { name: 'New Folder' })}
              className="text-[11px] font-medium text-blue-500 hover:text-blue-600"
            >
              + Add folder
            </button>
          </div>
          <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis, restrictToParentElement]} onDragEnd={handleFoldersDragEnd}>
            <SortableContext items={categories.map((c) => c.id)} strategy={verticalListSortingStrategy}>
              {categories.map((cat) => (
                <SortableRow key={cat.id} id={cat.id} className="group">
                  {({ listeners, attributes }: any) => (
                    <div className="flex items-center gap-2.5 rounded-lg px-1.5 py-[7px] hover:bg-zinc-50">
                      <DragHandle listeners={listeners} attributes={attributes} />
                      <div className="relative">
                        <button
                          onClick={() => setEditingFolderColor(editingFolderColor === cat.id ? null : cat.id)}
                          className="h-3.5 w-3.5 shrink-0 rounded-full border border-zinc-200 transition-transform hover:scale-110"
                          style={{ backgroundColor: getCategoryColor(cat.color) }}
                        />
                        {editingFolderColor === cat.id && (
                          <div className="absolute left-0 top-full z-10 mt-1 flex gap-1 rounded-lg border border-zinc-200 bg-white p-2 shadow-lg">
                            {TAG_COLOR_KEYS.map((c) => (
                              <ColorSwatch key={c} color={c} selected={cat.color === c} onClick={() => { voidAsync(updateCategory(cat.id, { color: c }), 'updateCategory (color)', { id: cat.id, color: c }); setEditingFolderColor(null) }} />
                            ))}
                          </div>
                        )}
                      </div>
                      {editingFolderId === cat.id ? (
                        <input
                          autoFocus
                          defaultValue={cat.name}
                          onBlur={(e) => {
                            const name = e.target.value.trim()
                            if (name && name !== cat.name) voidAsync(updateCategory(cat.id, { name }), 'updateCategory (rename)', { id: cat.id, name })
                            setEditingFolderId(null)
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                            if (e.key === 'Escape') setEditingFolderId(null)
                          }}
                          className="flex-1 rounded border border-blue-300 bg-blue-50/50 px-1.5 py-0.5 text-[12.5px] text-zinc-900 outline-none"
                        />
                      ) : (
                        <button onClick={() => onNavigateToFilter?.(`category:${cat.id}`)} className="flex-1 truncate text-left text-[12.5px] text-zinc-700 hover:text-blue-600 hover:underline">
                          {cat.name}
                        </button>
                      )}
                      <button onClick={() => setEditingFolderId(cat.id)} className="text-zinc-300 hover:text-zinc-500">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button onClick={() => voidAsync(deleteCategory(cat.id), 'deleteCategory', { id: cat.id })} className="text-zinc-300 hover:text-red-500">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    </div>
                  )}
                </SortableRow>
              ))}
            </SortableContext>
          </DndContext>
        </div>

        {/* Saved Filters (Inbox Composites — ADR 0029). Lets users save
         * the currently-active view (e.g. /tag/abc, /inbox/waiting-theirs)
         * as a named filter that shows up in the sidebar. MVP UI: list +
         * delete + "save current view as…" input. Complex multi-predicate
         * editing is deferred — users compose implicitly by navigating
         * to the view they want before saving. */}
        {onAddComposite && (
          <div className="border-t border-zinc-100 px-5 pb-3 pt-2">
            <div className="flex items-center justify-between pb-2 pt-1">
              <span className="text-[10px] font-medium tracking-wider text-zinc-400">FILTERS</span>
            </div>
            {composites.map((composite) => (
              <div key={composite.id} className="group flex items-center gap-2 rounded-lg px-1.5 py-1.5 hover:bg-zinc-50">
                {composite.icon ? (
                  <span className="w-4 shrink-0 text-center text-[12px] leading-none" aria-hidden="true">{composite.icon}</span>
                ) : (
                  <span className="h-3 w-3 shrink-0 rounded-full border border-zinc-200" style={{ backgroundColor: getCategoryColor(composite.color) }} />
                )}
                <button
                  onClick={() => onNavigateToFilter?.(`composite:${composite.id}`)}
                  className="flex-1 truncate text-left text-xs text-zinc-700 hover:text-blue-600 hover:underline"
                >
                  {composite.name}
                </button>
                <button
                  onClick={() => onDeleteComposite?.(composite.id)}
                  className="text-zinc-300 opacity-0 group-hover:opacity-100 hover:text-red-500"
                  title="Delete filter"
                  aria-label="Delete filter"
                >
                  <span className="text-[13px]">×</span>
                </button>
              </div>
            ))}
            {currentCompositeDefinition && currentCompositeDefinition.predicates.length > 0 ? (
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="text"
                  value={newCompositeName}
                  onChange={(e) => setNewCompositeName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newCompositeName.trim()) {
                      void onAddComposite({ name: newCompositeName.trim(), definition: currentCompositeDefinition })
                      setNewCompositeName('')
                    }
                  }}
                  placeholder="Name this filter..."
                  className="flex-1 rounded-md border border-zinc-200 px-2 py-1 text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
                />
                <button
                  onClick={() => {
                    if (newCompositeName.trim()) {
                      void onAddComposite({ name: newCompositeName.trim(), definition: currentCompositeDefinition })
                      setNewCompositeName('')
                    }
                  }}
                  className="text-[11px] font-medium text-blue-500 hover:text-blue-600"
                >
                  + Save view
                </button>
              </div>
            ) : (
              <p className="mt-1 px-1.5 text-[10px] italic leading-relaxed text-zinc-400">
                Navigate to a tag or Inbox view, then reopen this modal to save it as a filter.
              </p>
            )}
          </div>
        )}

        {/* Tags */}
        {onAddTag && (
          <div className="border-t border-zinc-100 px-5 pb-3 pt-2">
            <div className="flex items-center justify-between pb-2 pt-1">
              <span className="text-[10px] font-medium tracking-wider text-zinc-400">TAGS</span>
            </div>
            <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis, restrictToParentElement]} onDragEnd={handleTagsDragEnd}>
              <SortableContext items={tags.map((t) => t.id)} strategy={verticalListSortingStrategy}>
                {tags.map((tag) => (
                  <SortableRow key={tag.id} id={tag.id} className="group">
                    {({ listeners, attributes }: any) => (
                      <div className="flex items-center gap-2 rounded-lg px-1.5 py-1.5 hover:bg-zinc-50">
                        <DragHandle listeners={listeners} attributes={attributes} />
                        {/* Icon picker (ADR 0027). Click toggles the emoji grid;
                         * picking sets the icon, picking the same icon clears it. */}
                        <div className="relative">
                          <button
                            onClick={() => setTagEmojiPickerFor(tagEmojiPickerFor === tag.id ? null : tag.id)}
                            className={`flex h-5 w-5 items-center justify-center rounded-md text-[12px] ${
                              tagEmojiPickerFor === tag.id ? 'border border-blue-500 bg-blue-50' : 'hover:bg-zinc-100'
                            }`}
                            title={tag.icon ? 'Change icon' : 'Add icon'}
                            aria-label={tag.icon ? 'Change tag icon' : 'Add tag icon'}
                          >
                            {tag.icon || (
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-zinc-300">
                                <circle cx="12" cy="12" r="9" />
                              </svg>
                            )}
                          </button>
                          {tagEmojiPickerFor === tag.id && (
                            <div className="absolute left-0 top-full z-20 mt-1 w-56 rounded-xl border border-zinc-200 bg-white p-2 shadow-lg">
                              <div className="flex flex-wrap gap-0.5">
                                {EMOJI_GRID.flat().map((emoji) => (
                                  <button
                                    key={emoji}
                                    onClick={() => {
                                      const next = tag.icon === emoji ? '' : emoji
                                      onUpdateTag?.(tag.id, { icon: next })
                                      setTagEmojiPickerFor(null)
                                    }}
                                    className={`flex h-[26px] w-[26px] items-center justify-center rounded-md text-[14px] hover:bg-zinc-100 ${
                                      tag.icon === emoji ? 'bg-blue-50' : ''
                                    }`}
                                  >
                                    {emoji}
                                  </button>
                                ))}
                              </div>
                              {tag.icon && (
                                <button
                                  onClick={() => { onUpdateTag?.(tag.id, { icon: '' }); setTagEmojiPickerFor(null) }}
                                  className="mt-1 block w-full rounded px-2 py-1 text-center text-[11px] text-zinc-500 hover:bg-zinc-50"
                                >
                                  Remove icon
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="relative">
                          <button
                            onClick={() => setEditingTagColor(editingTagColor === tag.id ? null : tag.id)}
                            className="h-4 w-4 shrink-0 rounded-full border border-zinc-200 transition-transform hover:scale-110"
                            style={{ backgroundColor: getCategoryColor(tag.color) }}
                          />
                          {editingTagColor === tag.id && (
                            <div className="absolute left-0 top-full z-10 mt-1 flex gap-1 rounded-lg border border-zinc-200 bg-white p-2 shadow-lg">
                              {TAG_COLOR_KEYS.map((c) => (
                                <ColorSwatch key={c} color={c} selected={tag.color === c} onClick={() => { onUpdateTag?.(tag.id, { color: c }); setEditingTagColor(null) }} />
                              ))}
                            </div>
                          )}
                        </div>
                        {editingTagId === tag.id ? (
                          <input
                            autoFocus
                            defaultValue={tag.name}
                            onBlur={(e) => {
                              const name = e.target.value.trim()
                              if (name && name !== tag.name) onUpdateTag?.(tag.id, { name })
                              setEditingTagId(null)
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                              if (e.key === 'Escape') setEditingTagId(null)
                            }}
                            className="flex-1 rounded border border-blue-300 bg-blue-50/50 px-1.5 py-0.5 text-xs text-zinc-900 outline-none"
                          />
                        ) : (
                          <button onClick={() => onNavigateToFilter?.(`tag:${tag.id}`)} className="flex-1 truncate text-left text-xs text-zinc-700 hover:text-blue-600 hover:underline">
                            {tag.name}
                          </button>
                        )}
                        <button onClick={() => setEditingTagId(tag.id)} className="text-zinc-300 hover:text-zinc-500">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                        {/* Share-with-team toggle (ADR 0028). Only shown when:
                         *  1. The user is in a shared team at all — no UI for solo users.
                         *  2. The user created this tag — RLS lets creators *and* team
                         *     owners toggle, but the common shape is "creator shares/
                         *     unshares their own tag". Owner-override on someone else's
                         *     tag isn't exposed here; that can happen via admin tooling
                         *     later.
                         * Clicking flips team_id between the shared team's uuid and null.
                         */}
                        {sharedTeam && tag.user_id === user?.id && (
                          <button
                            onClick={() =>
                              onUpdateTag?.(tag.id, { team_id: tag.team_id ? null : sharedTeam.id })
                            }
                            className={tag.team_id ? 'text-blue-500 hover:text-blue-600' : 'text-zinc-300 hover:text-zinc-500'}
                            title={tag.team_id ? `Unshare from ${sharedTeam.name}` : `Share with ${sharedTeam.name}`}
                            aria-label={tag.team_id ? 'Unshare tag from team' : 'Share tag with team'}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                              <circle cx="9" cy="7" r="4" />
                              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                            </svg>
                          </button>
                        )}
                        <button onClick={() => onDeleteTag?.(tag.id)} className="text-zinc-300 hover:text-red-500">
                          <span className="text-[13px]">×</span>
                        </button>
                      </div>
                    )}
                  </SortableRow>
                ))}
              </SortableContext>
            </DndContext>
            <div className="mt-1 flex items-center gap-2">
              <input
                type="text"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newTagName.trim()) {
                    onAddTag(newTagName.trim(), TAG_COLOR_KEYS[tags.length % TAG_COLOR_KEYS.length])
                    setNewTagName('')
                  }
                }}
                placeholder="New tag name..."
                className="flex-1 rounded-md border border-zinc-200 px-2 py-1 text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
              <button
                onClick={() => {
                  if (newTagName.trim()) {
                    onAddTag(newTagName.trim(), TAG_COLOR_KEYS[tags.length % TAG_COLOR_KEYS.length])
                    setNewTagName('')
                  }
                }}
                className="text-[11px] font-medium text-blue-500 hover:text-blue-600"
              >
                + Add
              </button>
            </div>
          </div>
        )}

        {/* Footer hint */}
        <div className="border-t border-zinc-100 px-5 py-3">
          <span className="text-[11px] leading-relaxed text-zinc-400">
            Drag to reorder · Click a name to navigate · Click an emoji to change it
          </span>
        </div>
      </div>
    </div>
  )
}
